import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Eye, EyeOff, Loader2, Building2, User, Users } from 'lucide-react';
import { toast } from 'sonner';

const Register = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
    role: 'client',
    phone: '',
    company_name: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleRoleChange = (value) => {
    setFormData({ ...formData, role: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    
    if (formData.password.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    
    setLoading(true);
    
    try {
      const { confirmPassword, ...userData } = formData;
      const user = await register(userData);
      toast.success(`Bienvenue chez MSC, ${user.first_name} !`);
      
      // Redirect based on role
      if (user.role === 'agency') {
        navigate('/dashboard/agency');
      } else if (user.role === 'team_member') {
        navigate('/dashboard/team');
      } else {
        navigate('/dashboard/client');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'inscription');
    } finally {
      setLoading(false);
    }
  };

  const roleIcons = {
    client: <Building2 size={18} />,
    team_member: <User size={18} />,
    agency: <Users size={18} />
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#f8fafc] relative overflow-hidden py-8">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-1/2 -right-1/2 w-full h-full bg-gradient-to-br from-[#00d4aa]/10 to-transparent rounded-full" />
        <div className="absolute -bottom-1/2 -left-1/2 w-full h-full bg-gradient-to-tr from-[#1a365d]/10 to-transparent rounded-full" />
      </div>
      
      <Card className="w-full max-w-lg bg-white p-2 rounded-2xl shadow-xl z-10 relative animate-slide-up mx-4">
        <CardHeader className="space-y-1 text-center pb-4">
          {/* Logo */}
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-[#1a365d] rounded-xl flex items-center justify-center">
              <span className="text-[#00d4aa] font-bold text-2xl font-mono">MSC</span>
            </div>
          </div>
          <CardTitle className="text-2xl font-bold text-[#1a365d]">Rejoindre MSC</CardTitle>
          <CardDescription className="text-[#64748b]">
            Créez votre compte pour commencer
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name" className="text-[#1a365d]">Prénom</Label>
                <Input
                  id="first_name"
                  name="first_name"
                  data-testid="register-firstname-input"
                  placeholder="Jean"
                  value={formData.first_name}
                  onChange={handleChange}
                  className="input-default"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name" className="text-[#1a365d]">Nom</Label>
                <Input
                  id="last_name"
                  name="last_name"
                  data-testid="register-lastname-input"
                  placeholder="Dupont"
                  value={formData.last_name}
                  onChange={handleChange}
                  className="input-default"
                  required
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="username" className="text-[#1a365d]">Pseudo</Label>
              <Input
                id="username"
                name="username"
                data-testid="register-username-input"
                placeholder="jeandupont"
                value={formData.username}
                onChange={handleChange}
                className="input-default"
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#1a365d]">Email (optionnel)</Label>
              <Input
                id="email"
                name="email"
                type="email"
                data-testid="register-email-input"
                placeholder="votre@email.com"
                value={formData.email}
                onChange={handleChange}
                className="input-default"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="role" className="text-[#1a365d]">Type de compte</Label>
              <Select value={formData.role} onValueChange={handleRoleChange}>
                <SelectTrigger className="input-default" data-testid="register-role-select">
                  <SelectValue placeholder="Sélectionnez votre rôle" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="client">
                    <div className="flex items-center gap-2">
                      <Building2 size={16} />
                      <span>Client</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="team_member">
                    <div className="flex items-center gap-2">
                      <User size={16} />
                      <span>Équipier / Créatif</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {formData.role === 'client' && (
              <div className="space-y-2">
                <Label htmlFor="company_name" className="text-[#1a365d]">Nom de l'entreprise</Label>
                <Input
                  id="company_name"
                  name="company_name"
                  data-testid="register-company-input"
                  placeholder="Ma Société SAS"
                  value={formData.company_name}
                  onChange={handleChange}
                  className="input-default"
                />
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="phone" className="text-[#1a365d]">Téléphone</Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                data-testid="register-phone-input"
                placeholder="06 12 34 56 78"
                value={formData.phone}
                onChange={handleChange}
                className="input-default"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="password" className="text-[#1a365d]">Mot de passe</Label>
                <div className="relative">
                  <Input
                    id="password"
                    name="password"
                    data-testid="register-password-input"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={handleChange}
                    className="input-default pr-10"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748b] hover:text-[#1a365d]"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-[#1a365d]">Confirmer</Label>
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  data-testid="register-confirm-password-input"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className="input-default"
                  required
                />
              </div>
            </div>
            
            <Button
              type="submit"
              data-testid="register-submit-btn"
              disabled={loading}
              className="w-full btn-primary h-11 text-base"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Création...
                </>
              ) : (
                'Créer mon compte'
              )}
            </Button>
          </form>
          
          <div className="mt-6 text-center">
            <p className="text-[#64748b] text-sm">
              Déjà un compte ?{' '}
              <Link to="/login" className="text-[#00d4aa] hover:text-[#00b38f] font-semibold">
                Se connecter
              </Link>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Register;
